import React, { useEffect, useState } from 'react'

import PhatDropdown from 'components/formElements/PhatDropdown'
import LocalSettingToggle from 'components/accountSettingsPage/BrowserSpecificSiteSettings/LocalSettingToggle'

import {
  getImageSizePreference,
  setImageSizePreference,
  hideDefaultColorTagsKey,
} from 'services/accountSettings.service'

import styles from './browserSpecificSiteSettings.module.scss'

import { ImageSize } from 'utils/ImageUrl'

export const disableCollaborativeToastsKey = 'disable-collaborative-toasts'
export const enableTouchDragDeckPageKey = 'enable-touch-drag-deck-page'
export const rightCommanderKey = 'right-commander'
export const stickyDeckSearchKey = 'sticky-deck-search'
export const disableCloseOnClickKey = 'disableCloseOnClick'

// Cookie keys
export const disableExtraLayerKey = 'tb-disable-extra-layers'
export const hideTagRecommendationsKeyForNow = 'tbHideTagRecommendationsTemp'
export const permanentlyHideTagRecommendationsKey = 'tbHideTagRecommendationsPermanent'
export const stickyStackHeaders = 'tbStickyStackHeaders'

const BrowserSpecificSiteSettings = () => {
  const [localImagePreference, setLocalImagePreference] = useState<ImageSize>('normal')

  useEffect(() => {
    setLocalImagePreference(getImageSizePreference())
  }, [])

  const handleSetImagePreference = (value: ImageSize) => {
    setLocalImagePreference(value)
    setImageSizePreference(value)
  }

  return (
    <>
      <div className={styles.container}>
        <h2>Browser Specific Settings</h2>
        <p>
          These settings are created to be browser specific. That means that they will not follow your account around,
          but rather only be active to this specific web browser <b>only</b>. If you clear your cache, these settings
          will need to be set again.
        </p>

        <p>
          The purpose of these kinds of settings is to allow you to tailor your experience on Archidekt on a per device
          level (eg: phone vs desktop).
        </p>

        <div className={styles.options}>
          <LocalSettingToggle
            header="Disable collaborative messages"
            label="Disable the messages that appear when someone else makes a change to a deck you're viewing"
            cacheKey={disableCollaborativeToastsKey}
            cacheStyle="localStorage"
          />

          <LocalSettingToggle
            header="Enable drag and drop via touch input on deck pages"
            label="Enable drag and drop via a touch screen on deck pages (this was disabled by default due to it being a generally bad experience)"
            cacheKey={enableTouchDragDeckPageKey}
            cacheStyle="localStorage"
          />

          <LocalSettingToggle
            header="Disable extra card layer animations"
            label="Disabling the animated layers on cards for selected cards, focused cards, tags, etc. (Deck page only)"
            cacheKey={disableExtraLayerKey}
            cacheStyle="cookie"
          />

          <LocalSettingToggle
            header="Disable prompt to set deck tags"
            label="Prevents a menu reminding you to set deck tags from appearing on your deck pages."
            cacheKey={permanentlyHideTagRecommendationsKey}
            cacheStyle="cookie"
          />

          <span>
            <label className={styles.label}>Image resolution preference</label>
            <p>
              Mobile or slower devices may run better with lower resolution images. Higher resolution images go{' '}
              <i>burrrrr</i>
            </p>
            <PhatDropdown
              inlineHeader
              value={localImagePreference}
              options={[
                { label: 'Small', onClick: () => handleSetImagePreference('small'), id: 'small' },
                { label: 'Normal', onClick: () => handleSetImagePreference('normal'), id: 'normal' },
                { label: 'Large', onClick: () => handleSetImagePreference('large'), id: 'large' },
              ]}
            />
          </span>
        </div>
      </div>
      <div className={styles.container}>
        <h2>Experimental Site Settings</h2>
        <p>
          These are settings that we don't consider to be in their final form. They're stable and functional, but
          require either UI updates or additional supporting features to be fully finished.
        </p>

        <div className={styles.options}>
          <LocalSettingToggle
            header="Commander(s) on the Right"
            label="Move Commander (premium) categories above Sideboard/Maybeboard"
            cacheKey={rightCommanderKey}
            cacheStyle="localStorage"
          />

          <LocalSettingToggle
            header="Sticky Deck Search"
            label="Search bar always remains at the top of the page while searching for cards on the deck page"
            cacheKey={stickyDeckSearchKey}
            cacheStyle="localStorage"
          />

          <LocalSettingToggle
            header="Sticky group headers"
            label="On the deck page, group headers (eg: Categories, types, etc) will be stickied to the top of the page while scrolling down"
            cacheKey={stickyStackHeaders}
            cacheStyle="cookie"
          />

          <LocalSettingToggle
            header="Do not close dropdown on 2nd click"
            label="When clicking on an Archidekt dropdown, do not close the dropdown when clicking again"
            cacheKey={disableCloseOnClickKey}
            cacheStyle="localStorage"
          />

          <LocalSettingToggle
            header="Hide default color tags"
            label="Hide default color tags on the deck page"
            cacheKey={hideDefaultColorTagsKey}
            cacheStyle="cookie"
          />
        </div>
      </div>
    </>
  )
}

export default BrowserSpecificSiteSettings
